import * as bootstrap from 'bootstrap';
import imagesLoaded from 'imagesloaded';
import { DateTime } from 'luxon';
import Masonry from 'masonry-layout';
import * as clipboard from '../clipboard';
import * as html from '../html';
import Modal from '../modal';
import { MaybePromise } from '../promise';
import { RemToPx } from '../rem';
import * as table from './table';

export interface SortColumn<C> {
    column: C;
    direction: 'asc' | 'desc';
}

export interface Sort<C> {
    direction?: 'asc' | 'desc';
    column: C;
    columns?: SortColumn<C>[];
    index?: number;
    startAsc?: boolean;
}

export interface Image {
    name: string;
    src: string;
    url?: string;
}

export interface ColumnOptions<R, C, D> {
    name: string;
    draw?: (td: HTMLElement, row: R) => MaybePromise<unknown>;
    text?: (row: R) => MaybePromise<string>;

    visible?: boolean;
    columns?: C | C[];
    data?: D[];

    width: string | number;
    minWidth?: string;

    headingHTML?: string;
    headingClass?: string;

    overflowHidden?: boolean;

    sort?: Sort<C>;
    columnActions?: boolean;

    tooltip?: boolean | string;

    images?: (row: R) => Image[];
}

let headerTemplate: HTMLTableCellElement;
function getHeaderTemplate(): HTMLTableCellElement {
    if (headerTemplate) {
        return headerTemplate;
    }

    headerTemplate = document.getElementById('table-header-template') as HTMLTableCellElement;
    headerTemplate?.parentElement?.removeChild(headerTemplate);

    return headerTemplate;
}

export class Column<R extends table.GenericRow, C, D> {
    readonly Name: string;

    private draw?: (td: HTMLElement, row: R) => MaybePromise<unknown>;
    private text?: (row: R) => MaybePromise<string>;

    readonly HeadingHTML?: string;
    readonly HeadingClass?: string;
    readonly Width: string | number;
    readonly MinWidth?: string;
    Visible: boolean;
    readonly DefaultVisibility: boolean;
    readonly APIColumns: C[];
    readonly APIData: D[];
    readonly OverflowHidden: boolean;
    readonly Tooltip?: boolean | string;

    readonly Sort?: Sort<C>;
    private columnActions: boolean;

    Table?: table.Table<R, C, D>;

    private th?: HTMLTableCellElement;

    private asc?: HTMLElement;
    private desc?: HTMLElement;
    private index?: HTMLElement;

    HeaderDrawn?: (th: HTMLTableCellElement) => void;

    readonly Images?: (row: R) => Image[];

    Search?: HTMLInputElement;

    constructor(opts: ColumnOptions<R, C, D>) {
        this.Name = opts.name;
        this.draw = opts.draw ?? (opts.text ? (async (td, r) => { td.textContent = await opts.text!(r) }) : undefined);
        this.text = opts.text;

        if (opts.columns instanceof Array) {
            this.APIColumns = opts.columns;
        } else if (opts.columns) {
            this.APIColumns = [opts.columns];
        } else {
            this.APIColumns = [];
        }

        this.APIData = opts.data ?? [];

        if (this.APIColumns.length && !this.draw) {
            this.draw = (td, r) => td.textContent = (r as any)[this.APIColumns[0]];
        }

        this.OverflowHidden = opts.overflowHidden ?? true;

        const userVisibility = JSON.parse(window.localStorage.getItem(`${window.location.pathname}columnVisibility/${this.Name}`)!) as boolean | null;

        this.DefaultVisibility = opts.visible ?? true;
        this.Visible = userVisibility ?? this.DefaultVisibility;
        this.Width = opts.width;
        this.MinWidth = opts.minWidth;
        this.HeadingHTML = opts.headingHTML;
        this.HeadingClass = opts.headingClass;

        this.Sort = opts.sort;
        this.columnActions = opts.columnActions ?? true;

        this.Tooltip = opts.tooltip ?? (Boolean(this.HeadingHTML) || undefined);

        this.Images = opts.images;
    }

    Cell(row: R): HTMLTableCellElement {
        const td = document.createElement('td');
        const div = document.createElement('div');
        td.appendChild(div);

        if (!this.OverflowHidden) {
            div.classList.add('overflow-visible');
        } else {
            let t: bootstrap.Tooltip | undefined;
            div.addEventListener('mouseover', () => {
                if (!div.textContent) {
                    return;
                }

                if (div.offsetWidth < div.scrollWidth) {
                    if (!t) {
                        t = new bootstrap.Tooltip(div, {
                            title: div.textContent,
                        });
                        t.show();
                    }
                } else {
                    if (t) {
                        t.dispose();
                        t = undefined;
                    }
                }
            });
        }

        if (this.draw) {
            this.draw(div, row);
        }
        return td;
    }

    Header(): HTMLTableCellElement {
        this.th = getHeaderTemplate().cloneNode(true) as HTMLTableCellElement; // document.createElement('th');
        // const div = document.createElement('div');
        // this.th.appendChild(div);
        // const div = this.th.querySelector('div') as HTMLElement;
        const name = this.th.querySelector('.table-header-name') as HTMLElement;
        const actions = this.th.querySelector('.table-header-actions') as HTMLElement;

        if (this.columnActions) {
            const actionsLink = actions.querySelector('a') as HTMLAnchorElement;
            actionsLink.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
            });

            const searchLink = this.th.querySelector('.table-header-actions-search') as HTMLAnchorElement;
            searchLink.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();

                if (!this.Search) {
                    return;
                }

                this.Search.value = `${this.Search.value} ${this.APIColumns[0]}:""`.trim();

                const pos = this.Search.value.length - 1;

                this.Search.focus();
                this.Search.setSelectionRange(pos, pos);
            });

            const hasLink = this.th.querySelector('.table-header-actions-has') as HTMLAnchorElement;
            hasLink.querySelector('.table-header-actions-has-col')!.textContent = String(this.APIColumns[0]);
            hasLink.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();

                if (!this.Search) {
                    return;
                }

                this.Table?.Query.AddFilter('has', String(this.APIColumns[0]));
            });

            const copyLink = this.th.querySelector('.table-header-actions-copy') as HTMLAnchorElement;
            copyLink.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();

                if (!this.Table) {
                    return;
                }

                this.Table.CopyColumn(this);
            });

            const hideLink = this.th.querySelector('.table-header-actions-hide') as HTMLAnchorElement;
            hideLink.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();

                if (!this.Table) {
                    return;
                }

                this.Table.HideColumn(this);
            });

            const galleryLink = this.th.querySelector('.table-header-actions-gallery') as HTMLAnchorElement;
            if (!this.Images) {
                galleryLink.parentElement?.removeChild(galleryLink);
            } else {
                // const galleryContainer = document.querySelector('#col-gallery-container') as HTMLElement;
                // document.body.appendChild(galleryContainer);

                galleryLink.addEventListener('click', e => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!this.Table) {
                        return;
                    }

                    const body = document.createElement('div');
                    body.classList.add('col-gallery-grid');
                    const images = new Array<Image>().concat(...this.Table.GetRows().map(r => this.Images!(r)));

                    const gridSizer = document.createElement('div');
                    gridSizer.classList.add('grid-sizer');
                    body.appendChild(gridSizer);

                    for (const i of images) {
                        const img = `<img src="${html.Escape(i.src)}" alt="${html.Escape(i.name)}" title="${html.Escape(i.name)}">`;
                        let item: string;
                        if (i.url) {
                            item = `<a class="d-block grid-item" href="${html.Escape(i.url)}">${img}</a>`;
                        } else {
                            item = `<div class="grid-item">${img}</a>`;
                        }
                        body.appendChild(html.FromString(item));
                    }

                    new Modal({
                        size: 'modal-fullscreen',
                        body,
                    });

                    const msnry = new Masonry(body, {
                        itemSelector: '.grid-item',
                        columnWidth: '.grid-sizer',
                        percentPosition: true,
                        gutter: RemToPx(1),
                    });

                    imagesLoaded(body).on('progress', () => {
                        // layout Masonry after each image loads
                        msnry.layout!();
                    });
                });
            }
        } else {
            actions.parentElement?.removeChild(actions);
        }

        if (typeof this.HeadingHTML !== 'undefined') {
            name.innerHTML = this.HeadingHTML;
        } else {
            name.textContent = this.Name;
        }

        if (this.HeadingClass) {
            name.innerHTML = `<span class="${this.HeadingClass}">${name.innerHTML}</span>`;
        }

        if (this.Sort) {
            this.th.style.cursor = 'pointer';
            name.classList.add('d-flex', 'align-items-center', 'position-relative');

            this.asc = document.createElement('i');
            this.asc.classList.add('fas', 'fa-sort-up', 'ms-auto', 'd-none');
            name.appendChild(this.asc);

            this.desc = document.createElement('i');
            this.desc.classList.add('fas', 'fa-sort-down', 'ms-auto', 'd-none');
            name.appendChild(this.desc);

            this.index = document.createElement('div');
            this.index.classList.add('index', 'd-none');
            name.appendChild(this.index);

            if (this.Sort) {
                this.setSortIcons();
            }

            this.th.addEventListener('mousedown', e => {
                if (e.shiftKey) {
                    e.preventDefault();
                }
            });
            this.th.addEventListener('click', e => {
                if (!this.Sort!.direction) {
                    this.SetSortDirection(this.Sort!.startAsc ? 'asc' : 'desc');
                } else if (this.Sort!.direction === 'asc') {
                    if (e.shiftKey) {
                        this.SetSortDirection();
                    } else {
                        this.SetSortDirection('desc');
                    }
                } else {
                    this.SetSortDirection('asc');
                }

                if (!this.Table) {
                    return;
                }

                if (!e.shiftKey) {
                    this.Table.ClearSort(this);
                }
                this.Table.Send();
            });
        }

        if (this.Tooltip) {
            let tooltip = this.Tooltip;
            if (this.Tooltip === true) {
                tooltip = this.Name;
            }

            const _ = new bootstrap.Tooltip(name, {
                title: String(tooltip),
            });
        }

        if (this.HeaderDrawn) {
            this.HeaderDrawn(this.th);
        }

        return this.th;
    }

    SetSortDirection(direction?: 'asc' | 'desc') {
        if (this.Table && direction) {
            let newIndex = 0;
            for (const c of this.Table.GetColumns()) {
                if (c !== this && c.Sort?.direction) {
                    newIndex = Math.max(newIndex, c.Sort.index ?? 0);
                }
            }
            newIndex++;
            this.Sort!.index = newIndex;
        } else {
            delete this.Sort!.index;
        }

        this.Sort!.direction = direction;

        // since indexes just go up, we should reset them so that they start at 1
        // visibly in the table instead of starting at like 3 which would look confusing
        if (this.Table) {
            const columns = this.Table.GetColumns().filter(c => c.Sort?.direction);
            columns.sort((a, b) => a.Sort!.index! - b.Sort!.index!);
            let i = 0;
            for (const c of columns) {
                c.SetIndex(i++);
            }
        }

        this.setSortIcons();
    }

    SetIndex(index?: number) {
        if (this.Sort && this.index) {
            this.Sort.index = index;
            if (typeof index === 'undefined') {
                this.index.textContent = '';
            } else {
                this.index.textContent = String(index + 1);
            }
        }
    }

    ShowIndex() {
        this.index?.classList.remove('d-none');
    }

    HideIndex() {
        this.index?.classList.add('d-none');
    }

    private setSortIcons = () => {
        if (this.Sort?.direction === 'asc') {
            this.asc?.classList.remove('d-none');
            this.desc?.classList.add('d-none');
        } else if (this.Sort!.direction === 'desc') {
            this.asc?.classList.add('d-none');
            this.desc?.classList.remove('d-none');
        } else {
            this.asc?.classList.add('d-none');
            this.desc?.classList.add('d-none');
        }
    };

    async CellText(row: R): Promise<string> {
        if (!this.text) {
            return this.Cell(row).innerText.trim();
        }

        return this.text(row);
    }
}

interface ColumnDateOptions<R, C, D> extends Partial<ColumnOptions<R, C, D>> {
    name?: string;
    draw?: (td: HTMLElement, row: R) => void;
    column?: C;
    sortDirection?: 'asc' | 'desc';
    value?: (r: R) => string;
}

export class ColumnDateTime<R extends table.GenericRow, C, D> extends Column<R, C, D> {
    constructor(opts?: ColumnDateOptions<R, C, D>) {
        opts ??= {};
        opts.name ??= 'Created';
        opts.column ??= 'created' as unknown as C;
        opts.value ??= r => (r as any)[opts!.column!];
        opts.draw ??= (td, r) => {
            const d = DateTime.fromISO(opts!.value!(r));
            if (d.isValid) {
                td.textContent = d.toLocaleString(DateTime.DATETIME_SHORT);
            }
        };
        opts.text ??= r => {
            const d = DateTime.fromISO(opts!.value!(r));
            if (!d.isValid) {
                return '';
            }

            return d.toSQL({ includeOffset: false }) ?? '';
        };
        opts.visible ??= true;
        opts.width ||= '9.5rem';
        opts.columns ??= [opts.column];

        if (!opts.sort) {
            opts.sort = {
                column: opts.column,
                direction: opts.sortDirection,
            };
        }

        super(opts as ColumnOptions<R, C, D>);
    }
}

export class ColumnDate<R extends table.GenericRow, C, D> extends Column<R, C, D> {
    constructor(opts?: ColumnDateOptions<R, C, D>) {
        opts ??= {};
        opts.name ??= 'Created';
        opts.column ??= 'created' as unknown as C;
        opts.value ??= r => (r as any)[opts!.column!];
        opts.draw ??= (td, r) => {
            const d = DateTime.fromISO(opts!.value!(r));
            if (d.isValid) {
                td.textContent = d.toLocaleString(DateTime.DATE_SHORT);
            }
        };
        opts.text ??= r => {
            const d = DateTime.fromISO(opts!.value!(r));
            if (!d.isValid) {
                return '';
            }

            return d.toSQLDate() ?? '';
        };
        opts.visible ??= true;
        opts.width ||= '6rem';
        opts.columns ??= [opts.column];

        if (!opts.sort) {
            opts.sort = {
                column: opts.column,
                direction: opts.sortDirection,
            };
        }

        super(opts as ColumnOptions<R, C, D>);
    }
}

interface ColumnIDOptions<R, C, D> extends Partial<ColumnOptions<R, C, D>> {
    name?: string;
    draw?: (td: HTMLElement, row: R) => void;
    column?: C;
}

export class ColumnID<R extends table.GenericRow, C, D> extends Column<R, C, D> {
    constructor(opts?: ColumnIDOptions<R, C, D>) {
        opts ??= {};
        opts.name ??= 'ID';
        opts.column ??= 'id' as unknown as C;
        opts.text ??= (r: R) => (r as any)[opts!.column!];
        opts.draw = opts.draw ?? (async (td, r) => {
            const a = document.createElement('a');
            a.classList.add('text-body');
            a.textContent = await opts!.text!(r);
            a.href = `${window.location.protocol}//${window.location.host}${window.location.pathname}?q=${encodeURIComponent(`id:${opts!.text!(r)}`)}`;
            a.addEventListener('click', async e => {
                e.preventDefault();

                clipboard.Copy(await opts!.text!(r));
                const t = new bootstrap.Tooltip(a, {
                    title: 'Copied!',
                    trigger: 'manual',
                });
                t.show();
                setTimeout(() => t.dispose(), 2000);
            });

            td.appendChild(a);
        });
        opts.visible = opts.visible ?? false;
        opts.width = opts.width ?? '7.5rem';
        opts.columns = opts.columns ?? [opts.column];

        super(opts as ColumnOptions<R, C, D>);
    }
}

interface ColumnCheckboxOptions<R, C, D> extends Partial<ColumnOptions<R, C, D>> {
    name: string;
    column: C;
    editable?: boolean | ((r: R) => boolean);
    draw?: (td: HTMLElement, row: R) => void;
    click?: (r: R, checked: boolean, redrawCell: () => void, td: HTMLElement) => void;
    checkTooltip?: (r: R) => string;
}

export class ColumnCheckbox<R extends table.GenericRow, C, D> extends Column<R, C, D> {
    constructor(opts: ColumnCheckboxOptions<R, C, D>) {
        opts.editable = opts.editable;
        opts.draw ??= ((td, r) => {
            td.classList.add('text-center');

            const title = opts.checkTooltip ? opts.checkTooltip(r) : '';

            if (typeof opts.editable !== 'undefined') {
                const editable = typeof opts.editable === 'function' ? opts.editable(r) : opts.editable;
                if (!editable) {
                    if ((r as any)[opts.column]) {
                        const i = document.createElement('i');
                        i.classList.add('fas', 'fa-check');

                        if (title) {
                            const _ = new bootstrap.Tooltip(i, { title });
                        }

                        td.appendChild(i);
                    }
                    return;
                }
            }

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.classList.add('form-check-input');
            checkbox.checked = Boolean((r as any)[opts.column]);

            if (opts.click) {
                checkbox.addEventListener('click', () => opts.click!(r, checkbox.checked, () => opts.draw!(td, r), td));
            }

            if (title) {
                const _ = new bootstrap.Tooltip(checkbox, { title });
            }

            td.append(checkbox);
        });
        opts.text ??= r => (r as any)[opts.column] ? '1' : '0';
        opts.visible = opts.visible ?? true;
        opts.width = opts.width ?? '2rem';
        opts.columns = opts.columns ?? [opts.column];

        super(opts as ColumnOptions<R, C, D>);
    }
}

interface ColumnActionsOptions<R, C, D> extends Omit<ColumnOptions<R, C, D>, 'draw' | 'name' | 'sort' | 'width'> {
    name?: 'Actions';
    draw: (dropdown: HTMLUListElement, row: R) => void;
}

export class ColumnActions<R extends table.GenericRow, C, D> extends Column<R, C, D> {
    constructor(opts: ColumnActionsOptions<R, C, D>) {
        opts.name = "Actions";
        const draw = opts.draw;
        opts.draw = (td, r) => {
            td.classList.add('p-0', 'dropdown');

            const a = document.createElement('a');
            a.classList.add('d-flex', 'w-100', 'h-100', 'text-decoration-none', 'align-items-center', 'justify-content-center');
            a.href = '#';
            a.setAttribute('data-bs-toggle', 'dropdown');
            a.setAttribute('data-bs-offset', '0,-8'); // -.5rem

            const i = document.createElement('i');
            i.classList.add('fas', 'fa-ellipsis-v');
            a.append(i);
            td.append(a);

            const ul = document.createElement('ul');
            ul.classList.add('dropdown-menu');
            td.append(ul);

            draw(ul, r);
        };
        opts.text ??= () => '';
        opts.visible = opts.visible ?? true;
        (opts as ColumnOptions<R, C, D>).width = '2rem';
        opts.columns = opts.columns;
        opts.headingHTML = '';
        opts.overflowHidden = false;
        opts.columnActions = false;

        super(opts as ColumnOptions<R, C, D>);
    }
}

interface ColumnNumberOptions<R, C, D> extends Partial<ColumnOptions<R, C, D>> {
    column: C;
    toFixed?: number;
    dollarFormat?: boolean;
    negativeParens?: boolean;
    number?: (r: R) => number;
    percentage?: boolean;
    statsColumn?: C | false;
    width: string | number;
    ShowZero?: boolean;
}

export class ColumnNumber<R extends table.GenericRow, C, D> extends Column<R, C, D> {
    constructor(opts: ColumnNumberOptions<R, C, D>) {
        if (opts.dollarFormat && typeof opts.toFixed === 'undefined') {
            opts.toFixed = 2;
        }

        opts.negativeParens ??= true;

        opts.sort ??= {
            column: opts.column,
        };

        const format = (n: number): string => {
            const negative = n < 0;
            n = Math.abs(n);

            if (opts.percentage) {
                opts.dollarFormat = false;
                n *= 100;
            }

            let text = n.toLocaleString(undefined, { minimumFractionDigits: opts.toFixed, maximumFractionDigits: opts.toFixed });

            if (opts.dollarFormat) {
                text = `$${text}`;
            } else if (opts.percentage) {
                text = `${text}%`;
            }

            if (negative) {
                if (opts.negativeParens) {
                    text = `(${text})`;
                } else {
                    text = `-${text}`;
                }
            }

            return text;
        };

        opts.number ??= (r: R) => (r as any)[opts!.column!] as number;

        opts.text ??= r => format(opts.number!(r) || 0);
        opts.draw ??= async (td, r) => {
            const n = opts.number!(r);
            if (!n && !opts.ShowZero) {
                return;
            }

            td.classList.add('text-end');
            td.textContent = await opts.text!(r);
        };
        opts.columns ??= [opts.column];

        opts.headingClass ??= 'ms-auto';

        super(opts as ColumnOptions<R, C, D>);

        if (opts.statsColumn !== false) {
            this.HeaderDrawn = th => {
                const dropdown = th.querySelector('.dropdown-menu') as HTMLElement;
                const stats = th.querySelector('.table-header-stats') as HTMLElement;

                dropdown.parentElement?.addEventListener('show.bs.dropdown', () => {
                    stats.classList.remove('d-none');
                    stats.addEventListener('click', e => e.stopPropagation());

                    this.Table?.GetColumnStats(opts.statsColumn ? opts.statsColumn : opts.column).
                        then(colStats => {
                            stats.querySelector('.table-header-records')!.textContent = String(colStats.count);
                            stats.querySelector('.table-header-sum')!.textContent = format(colStats.sum);
                            stats.querySelector('.table-header-avg')!.textContent = format(colStats.avg);
                            stats.querySelector('.table-header-min')!.textContent = format(colStats.min);
                            stats.querySelector('.table-header-max')!.textContent = format(colStats.max);
                            stats.querySelector('.table-header-trimmed-10')!.textContent = format(colStats.trimmed10);
                            stats.querySelector('.table-header-trimmed-20')!.textContent = format(colStats.trimmed20);

                            stats.classList.add('loaded');
                        });
                });

                dropdown.parentElement?.addEventListener('hidden.bs.dropdown', () => stats.classList.remove('loaded'));
            };
        }
    }
}
